import { Component, OnInit, Pipe, PipeTransform } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { ProductsService } from '../../services/products.service';
import { EventEmitterService } from '../../services/event-emitter.service';
import { BsModalRef } from 'ngx-bootstrap/modal';

@Component({
  standalone: false,
  selector: 'app-product-details',
  templateUrl: './product-details.component.html',
  styleUrl: './product-details.component.scss',
})
export class ProductDetailsComponent implements OnInit {
  productId: string;
  businessName: string;
  product: any;
  quantity = 1;
  selectedIngredients: string[] = [];
  selectedOptions: any[] = [];
  canOrder: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productsService: ProductsService,
    private eventEmitterService: EventEmitterService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private bsModalRef: BsModalRef,
  ) {}

  ngOnInit() {
    console.log('productId: ', this.productId);
    this.getProduct(this.productId);
  }

  closeModal() {
    this.bsModalRef.hide();
  }

  getProduct(id: string) {
    this.businessName = localStorage.getItem('businessName')!;
    this.canOrder =
      localStorage.getItem(this.businessName + '-plan') == '1' ||
      localStorage.getItem(this.businessName + '-plan') == '2' ||
      localStorage.getItem(this.businessName + '-plan') == '3';
    this.productsService.getProductById(id).subscribe(
      (response) => {
        this.product = response;
        // No need to parse ingredients here as the pipe will handle it

        if (this.product.has_options == 'true') {
          this.product.options = JSON.parse(this.product.options);
          // Initialize selected options
          this.selectedOptions = this.product.options.map((option: any) => {
            const selectedOption = { ...option };

            if (option.type === 'select') {
              if (option.required) {
                // For required select options, find the cheapest choice
                const cheapestChoice = option.choices.reduce(
                  (prev: any, current: any) =>
                    Number(prev.price || 0) < Number(current.price || 0)
                      ? prev
                      : current,
                );
                selectedOption.choice = cheapestChoice;
              } else {
                selectedOption.choice = null;
              }
            } else if (option.type === 'multi-select') {
              if (option.required) {
                // For required multi-select options, select the cheapest choice
                const cheapestChoice = option.choices.reduce(
                  (prev: any, current: any) =>
                    Number(prev.price || 0) < Number(current.price || 0)
                      ? prev
                      : current,
                );
                selectedOption.choice = [cheapestChoice];
              } else {
                selectedOption.choice = [];
              }
            } else if (option.type === 'number') {
              if (option.required) {
                selectedOption.value = 1;
              } else {
                selectedOption.value = 0;
              }
            }

            return selectedOption;
          });
          // Calculate initial price
          this.updateProductPrice();
        } else {
          this.product.priceString = this.product.price;
        }
      },
      (error) => {
        console.error('Error loading product:', error);
        this.router.navigate(['/products']);
      },
    );
  }

  updateProductPrice() {
    let totalPrice = 0;

    // If product has options, start with 0, otherwise use base product price
    if (this.product.has_options == 'true') {
      this.selectedOptions.forEach((selectedOption: any) => {
        if (selectedOption.type === 'select' && selectedOption.choice) {
          totalPrice += Number(selectedOption.choice.price || 0);
        } else if (
          selectedOption.type === 'multi-select' &&
          selectedOption.choice
        ) {
          selectedOption.choice.forEach((choice: any) => {
            totalPrice += Number(choice.price || 0);
          });
        } else if (selectedOption.type === 'number' && selectedOption.value) {
          totalPrice +=
            Number(selectedOption.price || 0) * selectedOption.value;
        }
      });
    } else {
      totalPrice = Number(this.product.price);
    }

    this.product.priceString = Number(totalPrice).toFixed(2).toString();
  }

  toggleIngredient(ingredient: string) {
    const index = this.selectedIngredients.indexOf(ingredient);
    if (index === -1) {
      this.selectedIngredients.push(ingredient);
    } else {
      this.selectedIngredients.splice(index, 1);
    }
  }

  isIngredientSelected(ingredient: string): boolean {
    return this.selectedIngredients.includes(ingredient);
  }

  // Select option methods
  selectOption(option: any, choice: any) {
    const selectedOption = this.selectedOptions.find(
      (opt) => opt.name === option.name,
    );
    if (selectedOption) {
      selectedOption.choice = choice;
      this.updateProductPrice();
    }
  }

  isOptionSelected(option: any, choice: any): boolean {
    const selectedOption = this.selectedOptions.find(
      (opt) => opt.name === option.name,
    );
    return selectedOption && selectedOption.choice === choice;
  }

  // Multi-select option methods
  toggleMultiSelectOption(option: any, choice: any) {
    const selectedOption = this.selectedOptions.find(
      (opt) => opt.name === option.name,
    );
    if (selectedOption) {
      if (!selectedOption.choice) {
        selectedOption.choice = [];
      }

      const index = selectedOption.choice.findIndex(
        (item: any) => item.name === choice.name,
      );
      if (index === -1) {
        selectedOption.choice.push(choice);
      } else {
        selectedOption.choice.splice(index, 1);
      }
      this.updateProductPrice();
    }
  }

  isMultiSelectOptionSelected(option: any, choice: any): boolean {
    const selectedOption = this.selectedOptions.find(
      (opt) => opt.name === option.name,
    );
    return (
      selectedOption &&
      selectedOption.choice &&
      selectedOption.choice.some((item: any) => item.name === choice.name)
    );
  }

  // Number option methods
  incrementNumberOption(option: any) {
    const selectedOption = this.selectedOptions.find(
      (opt) => opt.name === option.name,
    );
    if (selectedOption) {
      if (!selectedOption.value) {
        selectedOption.value = 0;
      }
      selectedOption.value++;
      this.updateProductPrice();
    }
  }

  decrementNumberOption(option: any) {
    const selectedOption = this.selectedOptions.find(
      (opt) => opt.name === option.name,
    );
    if (selectedOption) {
      if (!selectedOption.value) {
        selectedOption.value = 0;
      }
      const minValue = option.required ? 1 : 0;
      if (selectedOption.value > minValue) {
        selectedOption.value--;
        this.updateProductPrice();
      }
    }
  }

  getNumberOptionValue(option: any): number {
    const selectedOption = this.selectedOptions.find(
      (opt) => opt.name === option.name,
    );
    return selectedOption ? selectedOption.value || 0 : 0;
  }

  decrementQuantity() {
    if (this.quantity > 1) {
      this.quantity--;
    }
  }

  incrementQuantity() {
    this.quantity++;
  }

  addToCart() {
    if (!this.product) return;

    // Validate required options
    if (this.product.has_options == 'true' && this.product.options) {
      for (const option of this.product.options) {
        if (option.required) {
          const selectedOption = this.selectedOptions.find(
            (opt) => opt.name === option.name,
          );

          if (
            option.type === 'select' &&
            (!selectedOption || !selectedOption.choice)
          ) {
            let summaryString = this.translateService.instant('error');
            let detailString =
              this.translateService.instant('please-select') +
              ' ' +
              option.name;
            this.messageService.add({
              severity: 'error',
              summary: summaryString,
              detail: detailString,
            });
            return;
          } else if (
            option.type === 'multi-select' &&
            (!selectedOption ||
              !selectedOption.choice ||
              selectedOption.choice.length === 0)
          ) {
            let summaryString = this.translateService.instant('error');
            let detailString =
              this.translateService.instant('please-select') +
              ' ' +
              option.name;
            this.messageService.add({
              severity: 'error',
              summary: summaryString,
              detail: detailString,
            });
            return;
          } else if (
            option.type === 'number' &&
            (!selectedOption ||
              !selectedOption.value ||
              selectedOption.value <= 0)
          ) {
            let summaryString = this.translateService.instant('error');
            let detailString =
              this.translateService.instant('please-specify') +
              ' ' +
              option.name;
            this.messageService.add({
              severity: 'error',
              summary: summaryString,
              detail: detailString,
            });
            return;
          }
        }
      }
    }

    const productToAdd = {
      ...this.product,
      quantity: this.quantity,
      selectedIngredients: [...this.selectedIngredients].sort(),
      selectedOptions: JSON.parse(JSON.stringify(this.selectedOptions)),
      price: this.product.priceString,
    };

    this.businessName = localStorage.getItem('businessName')!;
    let cart = JSON.parse(
      localStorage.getItem(this.businessName + '-cart') || '[]',
    );

    const index = cart.findIndex(
      (item: any) =>
        item.id === this.product.id &&
        JSON.stringify([...(item.selectedIngredients || [])].sort()) ===
          JSON.stringify(
            [...(productToAdd.selectedIngredients || [])].sort(),
          ) &&
        this.compareSelectedOptions(
          item.selectedOptions || [],
          productToAdd.selectedOptions || [],
        ),
    );

    if (index === -1) {
      cart.push(productToAdd);
    } else {
      cart[index].quantity += this.quantity;
    }

    localStorage.setItem(this.businessName + '-cart', JSON.stringify(cart));
    this.eventEmitterService.addedToCart();
    let summaryString = this.translateService.instant('success');
    let detailString = this.translateService.instant('item-added-to-cart');
    this.messageService.add({
      severity: 'success',
      summary: summaryString,
      detail: detailString,
    });
    this.closeModal();
  }

  compareSelectedOptions(options1: any[], options2: any[]): boolean {
    // If both are empty or null, they match
    if (
      (!options1 || options1.length === 0) &&
      (!options2 || options2.length === 0)
    ) {
      return true;
    }

    // If one is empty and the other isn't, they don't match
    if (!options1 || !options2 || options1.length !== options2.length) {
      return false;
    }

    // Compare each option by name
    for (const option1 of options1) {
      const option2 = options2.find((opt) => opt.name === option1.name);

      if (!option2) {
        return false; // Option not found in second array
      }

      // Compare based on option type
      if (option1.type !== option2.type) {
        return false;
      }

      if (option1.type === 'select') {
        // For select options, compare the selected choice
        if (!option1.choice && !option2.choice) {
          continue; // Both have no selection
        }
        if (!option1.choice || !option2.choice) {
          return false; // One has selection, other doesn't
        }
        if (option1.choice.name !== option2.choice.name) {
          return false; // Different choices selected
        }
      } else if (option1.type === 'multi-select') {
        // For multi-select options, compare arrays of choices (order doesn't matter)
        const choices1 = (option1.choice || []).map((c: any) => c.name).sort();
        const choices2 = (option2.choice || []).map((c: any) => c.name).sort();
        if (JSON.stringify(choices1) !== JSON.stringify(choices2)) {
          return false;
        }
      } else if (option1.type === 'number') {
        // For number options, compare the value
        if ((option1.value || 0) !== (option2.value || 0)) {
          return false;
        }
      }
    }

    return true; // All options match
  }

  goBack() {
    this.businessName = localStorage.getItem('businessName')!;
    const tableId = localStorage.getItem(this.businessName + '-table_id');

    let route: string;
    if (tableId) {
      route = `/${this.businessName}/table/${tableId}/products`;
    } else {
      route = `/${this.businessName}/products`;
    }

    this.router.navigate([route]);
  }
}
